import { ReactNode } from 'react';
import cx from 'classnames';
import copy from 'copy-to-clipboard';

import Button from '../Button';
import Icon from '../Icon';

import styles from './AddressDisplay.module.css';
import Box, { BoxStyleProps } from '../Box';

import { ReactComponent as CopySvg } from '../../../../icons/src/lib/copy.svg';
import Tooltip from '../Tooltip';
import Text from '../Text/Text';

export interface AddressDisplayProps extends BoxStyleProps {
  className?: string;
  logo?: ReactNode;
  address: string;
  highlight?: boolean;
  truncated?: boolean;
  copyTooltipContent?: string;
  title?: string;
  onCopy?: () => void;
}

export default function AddressDisplay({
  className,
  logo,
  address,
  copyTooltipContent,
  highlight = true,
  truncated = false,
  title,
  onCopy,
  ...otherProps
}: AddressDisplayProps): JSX.Element {
  const handleCopy = () => {
    copy(address);
    onCopy?.();
  };

  const letterCountFromHeadToEmphasize = 6;
  const letterCountFromEndToEmphasize = 4;

  const partsOfTheAddress = {
    head: address.slice(0, letterCountFromHeadToEmphasize),
    body: address.slice(
      letterCountFromHeadToEmphasize,
      address.length - letterCountFromEndToEmphasize
    ),
    tail: address.slice(-letterCountFromEndToEmphasize),
  };

  const styledAddress = (
    <>
      <b>{partsOfTheAddress.head}</b>
      {partsOfTheAddress.body}
      <b>{partsOfTheAddress.tail}</b>
    </>
  );

  return (
    <Box
      className={cx(styles['base'], className)}
      {...otherProps}
      data-test-id="address-display"
    >
      <div className={styles['main']}>
        {!!logo && <div className={styles['logo-wrapper']}>{logo}</div>}
        <div className={styles['content']}>
          <Text size="tiny" color="neutral-500" weight="600">
            {title || 'Payment address'}
          </Text>
          <div
            className={cx(styles['address'], {
              [styles['address--truncated']]: truncated,
            })}
            data-test-id="address-display-address"
          >
            {highlight ? styledAddress : address}
          </div>
        </div>
      </div>
      <Tooltip
        outerClassName={styles['tooltip-outer']}
        content={copyTooltipContent || 'Copy address'}
      >
        <Button
          data-test-id="copy-address-button"
          className={styles['copy-button']}
          contentClassName={styles['copy-button-content']}
          size="large"
          variant="secondary"
          onClick={handleCopy}
          aria-label="copy address"
        >
          <Icon size={20} component={<CopySvg />} />
        </Button>
      </Tooltip>
    </Box>
  );
}
